import { ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { requestStudentTuitionContinuityStatus } from '../../../api/requests/tuitionContinuity';
import { createFetchContext } from '../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../components/info/DisplayError';
import Loading from '../../../components/info/Loading';
import TableEmptyContent from '../../../components/text/TableEmptyContent';
import { StudentTuitionContinuityStatusResponse } from '../../../types/tuitionContinuity';

export interface TuitionContinuityLoaderProps<T> {
  studyPlanEnrollmentId: number;
  children: (props: { data: T; refresh: () => Promise<void> }) => ReactElement;
}

const { FetchProvider, FetchConsumer } = createFetchContext<
  undefined,
  StudentTuitionContinuityStatusResponse
>();

export const TuitionContinuityLoaderConsumer = FetchConsumer;

export default function TuitionContinuityLoader({
  studyPlanEnrollmentId,
  children,
}: TuitionContinuityLoaderProps<StudentTuitionContinuityStatusResponse>) {
  const { t } = useTranslation();
  const texts = useMemo(
    () => ({
      table: {
        notActivePeriod: {
          title: t('tuitionContinuity.notActivePeriod.title'),
          subtitle: t('tuitionContinuity.notActivePeriod.subtitle'),
        },
      },
    }),
    [t],
  );

  const request = useCallback(() => {
    return requestStudentTuitionContinuityStatus(studyPlanEnrollmentId);
  }, [studyPlanEnrollmentId]);

  return (
    <FetchProvider request={request} defaultQuery={undefined} fetchImmediately>
      <FetchConsumer>
        {({ data, loading, error, refresh }) => {
          if (error) {
            if (error.data.code === 'PERIOD_NOT_FOUND') {
              return (
                <TableEmptyContent
                  insideCard
                  title={texts.table.notActivePeriod.title}
                  subtitle={texts.table.notActivePeriod.subtitle}
                />
              );
            }
            return (
              <DisplayError
                insideCard
                body={error.code}
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          if (loading) {
            return <Loading insideCard />;
          }

          if (!data) {
            return (
              <DisplayError
                insideCard
                body="Data no cargada"
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          return children({ data, refresh });
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
